/**
 * Utility functions for consistent date formatting across all environments
 */

/**
 * Formats a date string (YYYY-MM-DD) to a consistent display format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string (MM/DD/YYYY)
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return "";

  // Parse the date as local time to avoid timezone issues
  const [year, month, day] = dateString.split("-").map(Number);

  // Validate the date components
  if (!year || !month || !day) return dateString;

  // Format as MM/DD/YYYY
  return `${month}/${day}/${year}`;
};

/**
 * Formats a date string for input fields (YYYY-MM-DD)
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Same date string (for input type="date")
 */
export const formatDateForInput = (dateString: string): string => {
  return dateString || "";
};

/**
 * Checks if a date is today
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns boolean
 */
export const isToday = (dateString: string): boolean => {
  if (!dateString) return false;

  const [year, month, day] = dateString.split("-").map(Number);
  const eventDate = new Date(year, month - 1, day);
  const today = new Date();

  return eventDate.toDateString() === today.toDateString();
};

/**
 * Checks if a date is in the past
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns boolean
 */
export const isPast = (dateString: string): boolean => {
  if (!dateString) return false;

  const [year, month, day] = dateString.split("-").map(Number);
  const eventDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return eventDate < today;
};

/**
 * Checks if a date is in the future
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns boolean
 */
export const isFuture = (dateString: string): boolean => {
  if (!dateString) return false;

  const [year, month, day] = dateString.split("-").map(Number);
  const eventDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return eventDate > today;
};
